import {
  BlockEditor,
  type BlockEditorCustomBlock,
  type BlockEditorImages,
  IconBanknote
} from '@sequencehq/core-components'
import {
  QUOTE_CONTAINER_WIDTH,
  type QuoteVariableType
} from '@sequencehq/quote-content'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useMemo } from 'react'
import { PricingWidget } from 'Cube/view/common/widgets/Pricing/PhasesWidget'
import { getVariableLabel } from '../utils/quoteVariables'
import { resolveFileUrl } from '../utils/resolveFileUrl'
import { ACCEPTED_IMAGE_MIME_TYPES, uploadFile } from '../utils/uploadFile'
import { PRICING_CUSTOM_BLOCK } from './adapters/presentationToTiptap'
import { useQuoteBlockEditor } from './useQuoteBlockEditor'
import { useQuoteVariableValues } from './useQuoteVariableValues'

const PLACEHOLDER = "Write, '/' for commands, '{' for variables..."

/**
 * The pricing stays editable for users who can't edit the rest of the content,
 * and its expanded phases live above the editor, so they survive the block
 * being moved.
 */
const CUSTOM_BLOCKS: BlockEditorCustomBlock[] = [
  {
    type: PRICING_CUSTOM_BLOCK,
    title: 'Pricing',
    icon: IconBanknote,
    isSingleton: true,
    isInteractiveWhenReadOnly: true,
    render: () => <PricingWidget data-testid="quote.blockEditor.phaseBlock" />
  }
]

/** A new table's 3 columns share the quote's width equally. */
const NEW_TABLE_COLUMN_WIDTH = Number.parseInt(QUOTE_CONTAINER_WIDTH, 10) / 3

const IMAGES: BlockEditorImages = {
  upload: uploadFile,
  resolveUrl: resolveFileUrl,
  acceptedMimeTypes: ACCEPTED_IMAGE_MIME_TYPES
}

export const QuoteBlockEditor = () => {
  const { editorKey, initialContent, onChange, isEditable, dimmed } =
    useQuoteBlockEditor()
  const variableValues = useQuoteVariableValues()
  const { useCustomerShippingAddresses } = useFeatureFlags()

  const variables = useMemo(
    () =>
      getQuoteVariables({
        isShippingAddressEnabled: useCustomerShippingAddresses
      }),
    [useCustomerShippingAddresses]
  )

  return (
    <BlockEditor
      key={editorKey}
      initialContent={initialContent}
      onChange={onChange}
      isEditable={isEditable}
      dimmed={dimmed}
      placeholder={PLACEHOLDER}
      variables={variables}
      variableValues={variableValues}
      customBlocks={CUSTOM_BLOCKS}
      images={IMAGES}
      newTableColumnWidth={NEW_TABLE_COLUMN_WIDTH}
      suggestionMenuTestId="quote.blockEditor.slashMenu"
      data-testid="quote.blockEditor"
    />
  )
}

const getQuoteVariables = ({
  isShippingAddressEnabled
}: {
  isShippingAddressEnabled: boolean
}) =>
  (
    [
      'CUSTOMER_NAME',
      'CUSTOMER_ADDRESS',
      ...(isShippingAddressEnabled
        ? (['CUSTOMER_SHIPPING_ADDRESS'] as const)
        : []),
      'CUSTOMER_COUNTRY',
      'RECIPIENTS',
      'QUOTE_OWNER',
      'START_DATE',
      'END_DATE',
      'DURATION',
      'PAYMENT_TERMS',
      'CURRENCY'
    ] satisfies QuoteVariableType[]
  ).map(type => ({ type, label: getVariableLabel(type) }))
